import React from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { cn } from "@/utils";

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  buttonType?: "primary" | "default" | "text" | "link";
  color?: "primary" | "default" | "danger";
  variant?: "outlined" | "solid" | "dashed";
  isLoading?: boolean;
}
export function Button(props: Props) {
  const {
    buttonType = "default",
    className,
    iconLeft,
    iconRight,
    disabled,
    isLoading,
    variant,
    color,
    children,
    ...rest
  } = props;

  const getCssForType = (buttonType: string) => {
    switch (buttonType) {
      case "primary":
        return "bg-cyan text-white hover:opacity-80 transaction-all duration-200 active:shadow-lg active:shadow-cyan/20";
      case "text":
        return "bg-transparent text-charleston_green hover:bg-gray-300/10 transaction-all duration-200 active:shadow-lg active:shadow-gray-300/20";
      case "link":
        return "bg-transparent text-cyan hover:opacity-80 active:text-cyan/80";
      default:
        return "bg-white text-charleston_green border border-gray-300 hover:!border-cyan hover:!text-cyan transaction-all duration-200 active:shadow-lg active:shadow-cyan/20";
    }
  };

  const getSccVariant = (
    variant: string,
    color: "primary" | "default" | "danger"
  ) => {
    switch (variant) {
      case "outlined":
        return color === "primary"
          ? "border border-cyan text-cyan hover:opacity-75 transaction-all duration-200 active:shadow-lg active:shadow-cyan/20"
          : color === "danger"
          ? "border border-error_main text-error_main hover:opacity-75 active:opacity-80 transaction-all duration-200 active:shadow-lg active:shadow-error_main/60"
          : `text-charleston_green border border-gray-300 transaction-all duration-200 active:shadow-lg active:shadow-cyan/20 ${
              !isLoading && "hover:!border-cyan hover:!text-cyan"
            }`;
      case "solid":
        return color === "primary"
          ? "bg-cyan text-white hover:opacity-80 transaction-all duration-200 active:shadow-lg active:shadow-cyan/20"
          : color === "danger"
          ? "bg-error_main text-white hover:opacity-80 active:bg-error_main/80 transaction-all duration-200 active:shadow-lg active:shadow-error_main/60"
          : "bg-black text-white hover:opacity-80 transaction-all duration-200 active:shadow-lg active:shadow-black/20";
      case "dashed":
        return color === "primary"
          ? "border border-dashed border-cyan text-cyan hover:opacity-75 transaction-all duration-200 active:shadow-lg active:shadow-cyan/20"
          : color === "danger"
          ? "border border-dashed border-error_main text-error_main hover:opacity-75 active:opacity-80 transaction-all duration-200 active:shadow-lg active:shadow-error_main/60"
          : "border border-dashed border-gray-300 text-charleston_green hover:!border-cyan hover:!text-cyan transaction-all duration-200 active:shadow-lg active:shadow-black/20";
      default:
        return "";
    }
  };

  return (
    <button
      {...rest}
      disabled={props.disabled || props.isLoading}
      className={cn(
        `h-9 px-4 text-sm font-bold leading-6 rounded-lg relative z-10 backdrop-grayscale-200 flex justify-center items-center gap-2 whitespace-nowrap
        ${disabled ? "!cursor-not-allowed !opacity-50" : ""}
        ${
          variant && color
            ? getSccVariant(variant, color)
            : getCssForType(buttonType)
        } ${className} 
        ${isLoading ? "!opacity-50 !cursor-wait" : ""}
        `
      )}
    >
      {iconLeft}
      {isLoading && (
        <AiOutlineLoading3Quarters className="animate-spin size-4 min-h-4 min-w-4" />
      )}
      {children}
      {iconRight}
    </button>
  );
}
